'use client';

// Shown when the root layout itself fails, which is the one case where none of
// the site's own chrome is available - no navbar, no footer, no fonts. It has
// to stand entirely on its own, which is why the styles here are inline rather
// than Tailwind classes: the stylesheet may be exactly what failed to load.
//
// It said "Something went wrong!" in English, with an exclamation mark, on a
// bilingual site. Both languages now, and no exclamation mark: a person whose
// page has just broken is not in the mood for enthusiasm.

import { useEffect } from 'react';
import { GOLD } from '@/lib/brand-colors';
import { reportClientError } from '@/lib/diagnostics/client';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    reportClientError(error, 'boundary');
  }, [error]);

  return (
    <html lang="en">
      <body style={{ margin: 0, background: '#000', color: '#fff', fontFamily: 'system-ui, sans-serif' }}>
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            textAlign: 'center',
          }}
        >
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 0.5rem' }}>
            Something went wrong on our side
          </h1>
          <h2 style={{ fontSize: '1.05rem', fontWeight: 600, color: '#d4d4d4', margin: '0 0 1.5rem' }}>
            Un problème est survenu de notre côté
          </h2>
          <p style={{ fontSize: '0.875rem', color: '#a3a3a3', maxWidth: '32rem', lineHeight: 1.6, margin: '0 0 2rem' }}>
            Nothing you did caused this, and nothing you were working on has been lost.
            <br />
            Vous n&apos;y êtes pour rien et rien de ce que vous faisiez n&apos;a été perdu.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={() => (typeof reset === 'function' ? reset() : window.location.reload())}
              style={{
                padding: '0.75rem 1.5rem',
                background: GOLD,
                color: '#000',
                fontWeight: 700,
                border: 'none',
                borderRadius: '0.75rem',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              Try again / Réessayer
            </button>
            {/* A plain anchor, deliberately. global-error replaces the root
                layout after React itself has failed, and next/link needs the
                router that may be the thing that broke. A full page load is
                the point: it rebuilds everything from scratch. */}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a
              href="/"
              style={{
                padding: '0.75rem 1.5rem',
                border: '1px solid #262626',
                color: '#d4d4d4',
                fontWeight: 600,
                borderRadius: '0.75rem',
                textDecoration: 'none',
                fontSize: '0.9rem',
              }}
            >
              Home / Accueil
            </a>
          </div>

          {/* The digest is what ties this to a line in the server log. Someone
              reporting the problem can quote it, and without it a report is
              "the site broke" with nothing to look for. */}
          {error?.digest && (
            <p style={{ fontSize: '0.7rem', color: '#525252', marginTop: '2.5rem', fontFamily: 'monospace' }}>
              {error.digest}
            </p>
          )}
        </div>
      </body>
    </html>
  );
}
